import React, { useState, useEffect } from "react";
import {
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    TextField,
    Button,
    Typography,
    Autocomplete,
    Chip,
    CircularProgress,
    Box,
    Alert,
    Collapse,
    Card,
    CardContent,
    Stack,
    Avatar,
    IconButton,
    Divider
} from '@mui/material';

// Icons
import PushPinIcon from '@mui/icons-material/PushPin';
import GroupIcon from '@mui/icons-material/Group';
import InfoIcon from '@mui/icons-material/Info';
import WarningIcon from '@mui/icons-material/Warning';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import PersonIcon from '@mui/icons-material/Person';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';

import api from "../../services/api";
import ToastError from "../../toast/error/toastError";
import { i18n } from "../../translate/i18n";

const ShareGroupModal = ({ open, onClose, onConfirm, ticket }) => {
    const [loading, setLoading] = useState(false);
    const [searchParam, setSearchParam] = useState('');
    const [options, setOptions] = useState([]);
    const [selectedUsers, setSelectedUsers] = useState([]);
    const [inputValue, setInputValue] = useState('');
    const [showDetails, setShowDetails] = useState(false);
    const [isEditMode, setIsEditMode] = useState(false);
    const [loadingShare, setLoadingShare] = useState(false);

    // Reset states when modal opens/closes
    useEffect(() => {
        if (!open) {
            setSearchParam('');
            setOptions([]);
            setSelectedUsers([]);
            setInputValue('');
            setLoading(false);
            setShowDetails(false);
            setIsEditMode(false);
            setLoadingShare(false);
        }
    }, [open]);

    // Load existing share data when opening in edit mode
    useEffect(() => {
        if (open && ticket?.isSharedGroup && ticket?.id) {
            setIsEditMode(true);
            setLoadingShare(true);

            const loadShareData = async () => {
                try {
                    const { data } = await api.get(`/ticket/${ticket.id}/share-group`);
                    if (data && data.users) {
                        setSelectedUsers(data.users);
                    }
                } catch (error) {
                    console.error("Erro ao carregar dados do compartilhamento:", error);
                    ToastError(error);
                } finally {
                    setLoadingShare(false);
                }
            };

            loadShareData();
        }
    }, [open, ticket?.id, ticket?.isSharedGroup]);

    // Search users with debounce
    useEffect(() => {
        if (!searchParam || searchParam.length < 2) {
            setOptions([]);
            setLoading(false);
            return;
        }

        setLoading(true);

        const delayDebounceFn = setTimeout(async () => {
            try {
                const { data } = await api.get("/users/", {
                    params: { searchParam },
                });

                const filteredUsers = (data.users || []).filter(
                    user => !selectedUsers.some(selected => selected.id === user.id)
                );

                setOptions(filteredUsers);
            } catch (err) {
                console.error("Erro ao buscar usuários:", err);
                ToastError(err);
                setOptions([]);
            } finally {
                setLoading(false);
            }
        }, 500);

        return () => clearTimeout(delayDebounceFn);
    }, [searchParam, selectedUsers]);

    const handleClose = () => {
        onClose();
    };

    const handleConfirm = async () => {
        if (selectedUsers.length === 0) return;

        setLoading(true);
        try {
            await onConfirm(selectedUsers, isEditMode);
            handleClose();
        } catch (error) {
            console.error("Erro ao compartilhar grupo:", error);
            ToastError(error);
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (event, newInputValue) => {
        setInputValue(newInputValue);
        setSearchParam(newInputValue);
    };

    const handleSelectionChange = (event, newValue) => {
        setSelectedUsers(newValue);
        if (newValue.length > selectedUsers.length) {
            setInputValue('');
            setSearchParam('');
        }
    };

    const removeUser = (userToRemove) => {
        setSelectedUsers(prev =>
            prev.filter(user => user.id !== userToRemove.id)
        );
    };

    const getInitials = (name) => {
        return name
            ?.split(' ')
            .map(word => word[0])
            .join('')
            .toUpperCase()
            .slice(0, 2) || '??';
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="md"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: 2,
                    maxHeight: '90vh',
                    width: { xs: '95%', sm: '600px', md: '700px' }
                }
            }}
        >
            <DialogTitle sx={{ pb: 1 }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <Stack direction="row" spacing={1.5} alignItems="center">
                        <PushPinIcon color="primary" />
                        <Typography variant="h6" component="h2">
                            {isEditMode ? 'Editar Compartilhamento' : 'Compartilhar e Fixar Grupo'}
                        </Typography>
                    </Stack>
                    <IconButton onClick={handleClose} size="small">
                        <CloseIcon />
                    </IconButton>
                </Stack>
            </DialogTitle>

            <DialogContent sx={{ pb: 2 }}>
                {loadingShare ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
                        <CircularProgress />
                        <Typography sx={{ ml: 2 }}>Carregando dados do compartilhamento...</Typography>
                    </Box>
                ) : (
                    <Stack spacing={3} sx={{ mt: 1 }}>
                        {/* Informações do Grupo */}
                        <Card variant="outlined">
                            <CardContent>
                                <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
                                    <GroupIcon color="primary" />
                                    <Typography variant="subtitle1" fontWeight="medium">
                                        Informações do Grupo
                                    </Typography>
                                </Stack>

                                <Stack spacing={2}>
                                    <Box>
                                        <Typography variant="caption" color="text.secondary" display="block">
                                            Contato/Grupo:
                                        </Typography>
                                        <Typography variant="body2" fontWeight="medium">
                                            {ticket?.contact?.name || 'Nome do contato'}
                                        </Typography>
                                    </Box>

                                    <Box>
                                        <Typography variant="caption" color="text.secondary" display="block">
                                            Ação:
                                        </Typography>
                                        <Typography variant="body2">
                                            {isEditMode
                                                ? 'Grupo já está fixado - edite os usuários com acesso'
                                                : 'Será fixado no topo para atendimento colaborativo'
                                            }
                                        </Typography>
                                    </Box>
                                </Stack>
                            </CardContent>
                        </Card>

                        {/* Aviso Importante */}
                        <Alert
                            severity="warning"
                            icon={<WarningIcon />}
                            sx={{ borderRadius: 1 }}
                        >
                            <Typography variant="subtitle2" gutterBottom>
                                Informações Importantes
                            </Typography>
                            <Typography variant="body2" sx={{ mb: 1 }}>
                                <strong>Atenção:</strong> Mensagens sairão do mesmo número.
                                Recomendamos assinar suas respostas.
                            </Typography>

                            <Button
                                size="small"
                                endIcon={showDetails ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                onClick={() => setShowDetails(!showDetails)}
                                sx={{ mt: 1, textTransform: 'none' }}
                            >
                                {showDetails ? 'Menos detalhes' : 'Ver detalhes'}
                            </Button>

                            <Collapse in={showDetails}>
                                <Box sx={{ mt: 2, pl: 2, borderLeft: 2, borderColor: 'warning.main' }}>
                                    <Stack spacing={0.5}>
                                        <Typography variant="body2">• Grupo fixado no topo da lista</Typography>
                                        <Typography variant="body2">• Prioridade sobre outros grupos</Typography>
                                        <Typography variant="body2">• Todos usuários podem responder</Typography>
                                        <Typography variant="body2">• Exemplo: <strong>"João"</strong>: Como posso ajudar?</Typography>
                                    </Stack>
                                </Box>
                            </Collapse>
                        </Alert>

                        {/* Busca de Usuários */}
                        <Box>
                            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                                <SearchIcon color="primary" />
                                <Typography variant="subtitle1" fontWeight="medium">
                                    Selecionar Usuários
                                </Typography>
                            </Stack>

                            <Autocomplete
                                multiple
                                size="small"
                                options={options}
                                getOptionLabel={(option) => option.name || ''}
                                value={selectedUsers}
                                onChange={handleSelectionChange}
                                inputValue={inputValue}
                                onInputChange={handleInputChange}
                                loading={loading}
                                noOptionsText={
                                    searchParam.length < 2
                                        ? "Digite 2+ caracteres"
                                        : loading
                                            ? "Buscando..."
                                            : "Nenhum usuário encontrado"
                                }
                                filterSelectedOptions={false}
                                isOptionEqualToValue={(option, value) => option.id === value.id}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Buscar usuários"
                                        placeholder="Digite o nome do usuário..."
                                        variant="outlined"
                                        fullWidth
                                        InputProps={{
                                            ...params.InputProps,
                                            endAdornment: (
                                                <>
                                                    {loading && <CircularProgress color="primary" size={20} />}
                                                    {params.InputProps.endAdornment}
                                                </>
                                            ),
                                        }}
                                    />
                                )}
                                renderOption={(props, option) => (
                                    <Box component="li" {...props} key={option.id}>
                                        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ py: 0.5 }}>
                                            <Avatar
                                                src={option.media}
                                                sx={{ width: 32, height: 32, fontSize: '0.8rem' }}
                                            >
                                                {getInitials(option.name)}
                                            </Avatar>
                                            <Box>
                                                <Typography variant="body2">
                                                    {option.name}
                                                </Typography>
                                                {option.email && (
                                                    <Typography variant="caption" color="text.secondary">
                                                        {option.email}
                                                    </Typography>
                                                )}
                                            </Box>
                                        </Stack>
                                    </Box>
                                )}
                                renderTags={(value, getTagProps) =>
                                    value.map((option, index) => (
                                        <Chip
                                            {...getTagProps({ index })}
                                            key={option.id}
                                            avatar={
                                                <Avatar
                                                    src={option?.media || ""}
                                                    sx={{ width: 24, height: 24, fontSize: '0.7rem' }}
                                                >
                                                    {getInitials(option.name)}
                                                </Avatar>
                                            }
                                            label={option.name}
                                            variant="outlined"
                                            size="small"
                                            sx={{
                                                maxWidth: '200px',
                                                '& .MuiChip-label': {
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'nowrap'
                                                }
                                            }}
                                        />
                                    ))
                                }
                            />
                        </Box>
                    </Stack>
                )}
            </DialogContent>

            <Divider />

            <DialogActions sx={{ p: 2.5 }}>
                <Stack
                    direction={{ xs: 'column-reverse', sm: 'row' }}
                    spacing={2}
                    sx={{ width: { xs: '100%', sm: 'auto' } }}
                >
                    <Button
                        variant="outlined"
                        onClick={handleClose}
                        sx={{ minWidth: { xs: '100%', sm: 120 } }}
                    >
                        Cancelar
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleConfirm}
                        disabled={selectedUsers.length === 0 || loading || loadingShare}
                        startIcon={
                            loading ?
                                <CircularProgress size={16} color="inherit" /> :
                                <PushPinIcon />
                        }
                        sx={{ minWidth: { xs: '100%', sm: 140 } }}
                    >
                        {loading
                            ? (isEditMode ? "Salvando..." : "Fixando...")
                            : (isEditMode ? `Salvar (${selectedUsers.length})` : `Fixar (${selectedUsers.length})`)
                        }
                    </Button>
                </Stack>
            </DialogActions>
        </Dialog>
    );
};

export default ShareGroupModal;